/**
 * Technician progress on a job, derived from the Phase 2 columns the Steddy app
 * writes through tech_set_job_progress / close_job
 * (migration 20260912100400_job_progress_columns.sql):
 *
 *   tech_ack_status   pending | accepted | declined
 *   tech_ack_at       when the tech accepted or declined
 *   tech_ack_reset_at set when a reschedule reset the acknowledgement
 *   en_route_at       "On my way"
 *   en_route_eta_*    minutes away the tech typed with "On my way", and when (20260917160000)
 *   arrived_at        "Start" (on site)
 *   work_finished_at  Finish wizard completed (close_job)
 *   hold_reason       reason given when the tech put the job on hold
 *
 * One derivation feeds the job page stepper, the jobs table badge, the
 * dashboard counter and the notifications, so they never disagree.
 */

export type TechProgressStage =
  | 'not_accepted'
  | 'rescheduled'
  | 'declined'
  | 'accepted'
  | 'en_route'
  | 'on_site'
  | 'finished';

export interface TechProgressSource {
  status: string;
  assigned_tech_id?: string | null;
  tech_ack_status?: string | null;
  tech_ack_at?: string | null;
  tech_ack_reset_at?: string | null;
  tech_decline_reason?: string | null;
  en_route_at?: string | null;
  en_route_eta_minutes?: number | null;
  en_route_eta_at?: string | null;
  arrived_at?: string | null;
  work_finished_at?: string | null;
  hold_reason?: string | null;
}

export interface TechProgress {
  stage: TechProgressStage;
  label: string;
  /** Badge variant from the design tokens (never raw palette classes). */
  variant: 'success' | 'warning' | 'info' | 'destructive-soft' | 'secondary' | 'outline';
  /** Timestamp that put the job in this stage, when one exists. */
  at: string | null;
  declineReason: string | null;
  holdReason: string | null;
  /** True when a reschedule cleared the acknowledgement and the tech has not answered yet. */
  awaitingReconfirm: boolean;
}

const LIVE_STATUSES = new Set(['scheduled', 'in_progress', 'on_hold', 'quote_sent']);

/**
 * The Jobs board tab a job belongs to. A scheduled job whose tech is on the
 * way is live work, so it sits with Ongoing rather than Scheduled.
 * useJobs mirrors this rule server-side for the 'in_progress' and 'scheduled' filters.
 */
export function jobBoardStatus(job: { status: string; en_route_at?: string | null }): string {
  return job.status === 'scheduled' && job.en_route_at ? 'in_progress' : job.status;
}

/**
 * Returns null when there is nothing to show: no technician, or a cancelled job,
 * or a job completed before the app existed (no work_finished_at).
 */
export function deriveTechProgress(job: TechProgressSource): TechProgress | null {
  if (!job.assigned_tech_id) return null;
  if (job.status === 'cancelled') return null;

  const base = {
    declineReason: job.tech_decline_reason ?? null,
    holdReason: job.status === 'on_hold' ? job.hold_reason ?? null : null,
    awaitingReconfirm: false,
  };

  if (job.work_finished_at) {
    return { stage: 'finished', label: 'Finished', variant: 'success', at: job.work_finished_at, ...base };
  }
  if (job.status === 'completed') return null;   // closed from the office before the app: nothing to say
  if (job.arrived_at) {
    return { stage: 'on_site', label: 'On site', variant: 'info', at: job.arrived_at, ...base };
  }
  if (job.en_route_at) {
    return { stage: 'en_route', label: 'En route', variant: 'info', at: job.en_route_at, ...base };
  }
  if (job.tech_ack_status === 'declined') {
    return { stage: 'declined', label: 'Declined', variant: 'destructive-soft', at: job.tech_ack_at ?? null, ...base };
  }
  if (job.tech_ack_status === 'accepted') {
    return { stage: 'accepted', label: 'Accepted', variant: 'success', at: job.tech_ack_at ?? null, ...base };
  }
  if (!LIVE_STATUSES.has(job.status)) return null;
  if (job.tech_ack_reset_at) {
    return {
      stage: 'rescheduled',
      label: 'Awaiting tech confirmation',
      variant: 'warning',
      at: job.tech_ack_reset_at,
      ...base,
      awaitingReconfirm: true,
    };
  }
  return { stage: 'not_accepted', label: 'Not accepted', variant: 'warning', at: null, ...base };
}

/** Jobs the technician declined from the app; they keep assigned_tech_id, so "unassigned" does not catch them. */
export function isDeclinedByTech(job: TechProgressSource): boolean {
  return !!job.assigned_tech_id && job.tech_ack_status === 'declined' && LIVE_STATUSES.has(job.status);
}

/** The ETA the technician gave with "On my way", while they are still on the road. */
export interface EnRouteEta {
  minutes: number;
  /** When the tech sent it. */
  sentAt: string;
  /** sentAt + minutes. */
  expectedAt: Date;
  /** Past the expected time and not on site yet. */
  late: boolean;
}

export function enRouteEta(job: TechProgressSource, now: Date = new Date()): EnRouteEta | null {
  if (!job.en_route_at || job.arrived_at || job.work_finished_at) return null;
  if (job.status === 'completed' || job.status === 'cancelled') return null;
  if (!job.en_route_eta_minutes || !job.en_route_eta_at) return null;
  const expectedAt = new Date(new Date(job.en_route_eta_at).getTime() + job.en_route_eta_minutes * 60_000);
  return {
    minutes: job.en_route_eta_minutes,
    sentAt: job.en_route_eta_at,
    expectedAt,
    late: now.getTime() > expectedAt.getTime(),
  };
}

/** The four steps of the job-page stepper, in order, with the timestamp each one carries. */
export interface TechProgressStep {
  key: 'accepted' | 'en_route' | 'on_site' | 'finished';
  label: string;
  at: string | null;
  state: 'done' | 'current' | 'todo' | 'blocked';
}

export function techProgressSteps(job: TechProgressSource): TechProgressStep[] {
  const accepted = job.tech_ack_status === 'accepted' || !!job.en_route_at || !!job.arrived_at || !!job.work_finished_at;
  const declined = job.tech_ack_status === 'declined' && !job.arrived_at;
  const steps: TechProgressStep[] = [
    { key: 'accepted', label: 'Accepted', at: accepted ? job.tech_ack_at ?? null : null, state: 'todo' },
    { key: 'en_route', label: 'En route', at: job.en_route_at ?? null, state: 'todo' },
    { key: 'on_site', label: 'On site', at: job.arrived_at ?? null, state: 'todo' },
    { key: 'finished', label: 'Finished', at: job.work_finished_at ?? null, state: 'todo' },
  ];
  const done = [accepted, !!job.en_route_at, !!job.arrived_at, !!job.work_finished_at];
  let currentSet = false;
  steps.forEach((s, i) => {
    if (done[i]) {
      s.state = 'done';
    } else if (declined && i === 0) {
      s.state = 'blocked';
      currentSet = true;
    } else if (!currentSet) {
      s.state = 'current';
      currentSet = true;
    }
  });
  return steps;
}
